/**
 * Shopping Cart Page - WoodAlps E-commerce Cart Management
 * 
 * This comprehensive cart page provides full e-commerce functionality:
 * - Real-time cart item display and management
 * - Quantity adjustment with validation and limits
 * - Item removal with confirmation
 * - Cart total calculations and pricing display
 * - Local storage persistence across sessions
 * - Responsive design for all devices
 * - Error handling and user feedback
 * 
 * Cart Features:
 * - Client-side cart state management
 * - Quantity limits and validation alerts
 * - Image optimization for product thumbnails
 * - Cart clearing functionality for testing
 * - Navigation to checkout/quote process
 * - Empty cart state with call-to-action
 * 
 * State Management:
 * - Local storage integration for persistence
 * - Real-time cart updates and calculations
 * - Loading states and error handling
 * 
 * @description E-commerce shopping cart with full item management functionality
 */

import { useState, useEffect } from 'react';
import Head from 'next/head';
import Link from 'next/link';
import Image from 'next/image';
import { useRouter } from 'next/router';
import { Trash2, X, Minus, Plus, AlertCircle, Info, ArrowLeft } from 'lucide-react';
import Layout from '../components/layout/Layout';
import { 
  getCartItems, 
  removeCartItem, 
  updateCartItemQuantity, 
  clearCart 
} from '../src/utils/cart';
import { getProductImageUrl } from '../src/utils/imageUtils';
import { useTranslation } from '../src/hooks/useTranslation';

export default function Cart() {
  const router = useRouter();
  const [cartItems, setCartItems] = useState([]);
  const [isClient, setIsClient] = useState(false);
  const [showLimitAlert, setShowLimitAlert] = useState(false);
  const [limitMessage, setLimitMessage] = useState('');
  const { t } = useTranslation();
  // Initialize from local storage once the component mounts on client
  useEffect(() => {
    setIsClient(true);
    
    // Load cart items using our utility function
    const items = getCartItems();
    console.log('Cart Items Loaded:', items);
    
    // Add debug info for each item's images
    items.forEach(item => {
      console.log(`Item ${item.id} image details:`, {
        hasCoverImage: !!item.coverImage,
        coverImageType: item.coverImage ? typeof item.coverImage : 'none',
        hasCardImages: !!item.cardImages,
        cardImagesLength: item.cardImages ? item.cardImages.length : 0,
        hasImage: !!item.image,
        imageType: item.image ? typeof item.image : 'none'
      });
    });
    
    // For testing: Clear cart if URL has ?clearcart=true
    if (typeof window !== 'undefined' && window.location.search.includes('clearcart=true')) {
      console.log('Clearing cart for testing purposes');
      clearCart();
      window.location.href = window.location.pathname; // Redirect to remove the query parameter
      return;
    }
    
    setCartItems(items);
    
    // Listen for cart updates from other components
    const handleCartUpdate = () => {
      setCartItems(getCartItems());
    };
    
    window.addEventListener('cart-updated', handleCartUpdate);
    
    return () => {
      window.removeEventListener('cart-updated', handleCartUpdate);
    };
  }, []);

  // Calculate total cost of items in cart
  const calculateTotal = () => {
    return cartItems.reduce((total, item) => {
      return total + (item.price * item.quantity);
    }, 0);
  };

  // Format price with space as thousand separator
  const formatPrice = (price) => {
    return price.toString().replace(/\B(?=(\d{3})+(?!\d))/g, " ");
  };

  // Helper function to format product type display consistently
  const getFormattedProductType = (item) => {
    // Default fallbacks if missing
    const productType = item.productType || 'frame';
    
    // Handle missing or empty category by trying to extract from title
    let category = '';
    if (item.category && item.category.trim() !== '') {
      category = item.category;
    } else if (item.title) {
      // Try to extract category from title
      const titleLower = item.title.toLowerCase();
      if (titleLower.includes('mtb') || titleLower.includes('yggy')) {
        category = 'mtb';
      } else if (titleLower.includes('road') || titleLower.includes('grooty')) {
        category = 'road';
      } else if (titleLower.includes('gravel') || titleLower.includes('branchy')) {
        category = 'gravel';
      }
    }
    
    // If still no category after extraction, use a generic 'wooden'
    if (!category) {
      category = 'wooden';
    }
    
    // Use translation keys for categories and product types
    const translatedCategory = t(`product.categories.${category.toLowerCase()}`);
    const translatedType = t(`product.types.${productType.toLowerCase()}`);
    
    // Return formatted product type (e.g., "MTB Frame" or "Road Bike")
    return `${translatedCategory} ${translatedType}`;
  };

  // Handle quantity changes with type-based limits
  const updateQuantity = (item, newQuantity) => {
    // Use our utility to update quantity with product type limits
    const success = updateCartItemQuantity(
      item.id, 
      newQuantity, 
      { size: item.size, color: item.color }
    );
    
    if (!success) {
      // Show limit alert message
      if (item.productType === 'bike') {
        setLimitMessage('Maximum 1 unit allowed for bike products');
      } else if (item.productType === 'frame') {
        setLimitMessage('Maximum 3 units allowed for frame products');
      } else {
        setLimitMessage('Cannot update quantity');
      }
      
      setShowLimitAlert(true);
      setTimeout(() => setShowLimitAlert(false), 3000);
    } else {
      // Update cart items in state from storage to reflect changes
      setCartItems(getCartItems());
    }
  };

  // Remove item from cart
  const removeItem = (item) => {
    removeCartItem(item.id, { size: item.size, color: item.color });
    setCartItems(getCartItems());
  };

  // Calculate for empty cart
  const isCartEmpty = cartItems.length === 0;
  
  // Get a default image based on product category/title
  const getCategoryDefaultImage = (item) => {
    // If title explicitly contains MTB or Woody, use MTB image
    if (item.title && (item.title.toLowerCase().includes('mtb') || item.title.toLowerCase().includes('woody'))) {
      return "/images/mtb-frame-product-cover.jpg";
    }
    
    const category = (item.category || '').toLowerCase();
    const title = (item.title || '').toLowerCase();
    
    if (category === 'mtb') {
      return "/images/mtb-frame-product-cover.jpg";
    } else if (category === 'road' || title.includes('grooty')) {
      return "/images/road-frame-product.jpg";
    } else if (category === 'gravel' || title.includes('branchy')) {
      return "/images/gravel-frame-product.jpg";
    }
    
    // As a last resort, check if title has any hints
    if (title.includes('mtb') || title.includes('mountain')) {
      return "/images/mtb-frame-product-cover.jpg";
    }
    
    // Default fallback
    return '/images/color-placeholder.svg';
  };

  return (
    <>
      <Head>
        <title>{t('cart.meta_title')}</title>
        <meta 
          name="description" 
          content={t('cart.meta_description')}
        />
      </Head>

      <div className="container-custom py-16 md:py-24">
        <h1 className="text-3xl md:text-4xl font-bold mb-4 font-neuropol text-center">{t('cart.title')}</h1>
        <div className="w-16 h-1 bg-accent-color mx-auto mb-12"></div>

        {/* Alert for quantity limits */}
        {showLimitAlert && (
          <div className="fixed top-24 left-1/2 transform -translate-x-1/2 bg-white text-black px-6 py-3 rounded-lg shadow-lg z-50 flex items-center">
            <AlertCircle size={20} className="mr-2 text-accent-color" />
            <span>{limitMessage}</span>
          </div>
        )}

        {isClient ? (
          <>
            {isCartEmpty ? (
              <div className="text-center py-16 bg-wood-brown/5 rounded-lg">
                <div className="mb-6">
                  <Image 
                    src="/images/empty-cart.svg" 
                    alt="Empty Cart" 
                    width={120} 
                    height={120}
                    className="mx-auto opacity-50"
                  />
                </div>
                <h2 className="text-2xl font-bold mb-4">{t('cart.empty_cart_title')}</h2>
                <p className="text-gray-600 mb-8">{t('cart.empty_cart_description')}</p>
                <div className="flex flex-wrap justify-center gap-4">
                  <Link href="/road" className="px-6 py-3 bg-black text-white font-bold rounded-md hover:bg-accent-color transition-colors">
                    {t('cart.explore_road_bikes')}
                  </Link>
                  <Link href="/gravel" className="px-6 py-3 bg-black text-white font-bold rounded-md hover:bg-accent-color transition-colors">
                    {t('cart.explore_gravel_bikes')}
                  </Link>
                  <Link href="/mtb" className="px-6 py-3 bg-black text-white font-bold rounded-md hover:bg-accent-color transition-colors">
                    {t('cart.explore_mtb_bikes')}
                  </Link>
                </div>
              </div>
            ) : (
              <div className="flex flex-col lg:flex-row gap-8">
                {/* Cart Items */}
                <div className="lg:w-2/3">
                  <div className="bg-white rounded-lg shadow-sm">
                    {/* Header */}
                    <div className="hidden md:grid md:grid-cols-12 gap-4 p-4 border-b border-gray-200 text-sm font-bold">
                      <div className="md:col-span-6">{t('cart.product')}</div>
                      <div className="md:col-span-2 text-center">{t('cart.price')}</div>
                      <div className="md:col-span-2 text-center">{t('cart.quantity')}</div>
                      <div className="md:col-span-2 flex justify-between items-center">
                        <span className="text-center">{t('cart.total')}</span>
                        <span className="text-center invisible">{t('cart.delete')}</span>
                      </div>
                    </div>

                    {/* Cart Items */}
                    {cartItems.map((item) => (
                      <div key={`${item.id}-${item.size}-${item.color}`} className="border-b border-gray-200 last:border-b-0">
                        <div className="md:grid md:grid-cols-12 gap-4 p-4 items-center">
                          {/* Mobile Remove Button */}
                          <div className="md:hidden absolute top-4 right-4">
                            <button
                              onClick={() => removeItem(item)}
                              className="text-gray-400 hover:text-accent-color transition-colors"
                              aria-label={t('cart.remove_item')}
                            >
                              <X size={18} />
                            </button>
                          </div>

                          {/* Product Info */}
                          <div className="md:col-span-6 flex items-center">
                            <div className="w-32 h-20 md:w-40 md:h-24 bg-gray-100 rounded-md overflow-hidden mr-4 flex-shrink-0">
                              {console.log('Cart item image data:', item.id, { 
                                coverImage: item.coverImage, 
                                hasCardImages: !!item.cardImages, 
                                cardImagesLength: item.cardImages?.length || 0,
                                cardImagesData: item.cardImages?.[0],
                                image: item.image,
                                category: item.category,
                                title: item.title,
                                productType: item.productType,
                                uniqueProductId: item.uniqueProductId
                              })}
                              
                              {/* Helper function to get the best image URL */}
                              {(() => {
                                const getImageUrl = () => {
                                  // First try direct coverImage property with the unique product ID
                                  if (item.coverImage) return item.coverImage;
                                  
                                  // Fall back to category default + uniqueProductId if available
                                  const defaultImage = getCategoryDefaultImage(item);
                                  
                                  if (!item.uniqueProductId) {
                                    return defaultImage;
                                  }
                                  
                                  // Use the utility function to safely append parameters
                                  return getProductImageUrl(defaultImage, item.uniqueProductId);
                                };
                                
                                return (
                                  <Image
                                    src={getImageUrl()}
                                    alt={item.title || 'Product'}
                                    width={160}
                                    height={100}
                                    className="w-full h-full object-cover"
                                    priority={true}
                                    onError={(e) => {
                                      console.error('Failed to load image:', {
                                        attemptedSrc: getImageUrl(),
                                        itemId: item.id,
                                        itemTitle: item.title,
                                        uniqueProductId: item.uniqueProductId,
                                        fallbackUsed: getCategoryDefaultImage(item)
                                      });
                                      // Try with the appropriate category default image
                                      e.target.src = getCategoryDefaultImage(item);
                                    }}
                                  />
                                );
                              })()}
                            </div>
                            <div className="flex-grow pr-8 md:pr-0">
                              <Link href={`/product/${item.slug || item.id}`} className="block">
                                <h3 className="font-bold font-neuropol text-base md:text-lg mb-1 hover:text-accent-color transition-colors">
                                  {item.title}
                                </h3>
                              </Link>
                              <div className="text-sm text-gray-600 mb-2">
                                <span>{t('cart.size')}: {item.size}</span>
                              </div>
                              <div className="text-sm text-gray-600 flex items-center">
                                <Info size={14} className="mr-1" />
                                <span>
                                  {item.engraving ? 
                                    t('cart.personalized_engraving_included') : 
                                    t('cart.no_personalized_engraving')
                                  }
                                </span>
                              </div>
                              <div className="text-sm text-gray-500 mt-1">
                                <span>
                                  {getFormattedProductType(item)}
                                </span>
                              </div>
                            </div>
                          </div>

                          {/* Price */}
                          <div className="md:col-span-2 text-center mt-4 md:mt-0 flex items-center justify-center">
                            <div className="md:hidden text-sm text-gray-500 mb-1">{t('cart.price')}:</div>
                            <div className="font-semibold">{formatPrice(item.price)} €</div>
                          </div>

                          {/* Quantity */}
                          <div className="md:col-span-2 text-center mt-4 md:mt-0">
                            <div className="md:hidden text-sm text-gray-500 mb-1">{t('cart.quantity')}:</div>
                            <div className="flex items-center justify-center">
                              <button
                                onClick={() => updateQuantity(item, item.quantity - 1)}
                                className="w-8 h-8 rounded-md border border-gray-300 flex items-center justify-center hover:bg-gray-100"
                                disabled={item.quantity <= 1}
                                aria-label={t('cart.decrease_quantity')}
                              >
                                <Minus size={16} />
                              </button>
                              <span className="mx-3 w-6 text-center">{item.quantity}</span>
                              <button
                                onClick={() => updateQuantity(item, item.quantity + 1)}
                                className="w-8 h-8 rounded-md border border-gray-300 flex items-center justify-center hover:bg-gray-100"
                                aria-label={t('cart.increase_quantity')}
                              >
                                <Plus size={16} />
                              </button>
                            </div>
                          </div>

                          {/* Total */}
                          <div className="md:col-span-2 text-center mt-4 md:mt-0 flex items-center justify-between">
                            <div className="md:hidden text-sm text-gray-500 mb-1">{t('cart.total')}:</div>
                            <div className="font-bold">{formatPrice(item.price * item.quantity)} €</div>
                            {/* Desktop Remove Button - now aligned with Total */}
                            <div className="flex justify-end w-6">
                              <button
                                onClick={() => removeItem(item)}
                                className="hidden md:block text-gray-400 hover:text-accent-color transition-colors"
                                aria-label={t('cart.remove_item')}
                              >
                                <Trash2 size={18} />
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Action Buttons */}
                  <div className="mt-6 flex justify-between">
                    <Link 
                      href="/" 
                      className="inline-flex items-center text-accent-color hover:underline transition-colors"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M9.707 14.707a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 1.414L7.414 9H15a1 1 0 110 2H7.414l2.293 2.293a1 1 0 010 1.414z" clipRule="evenodd" />
                      </svg>
                      {t('cart.continue_shopping')}
                    </Link>
                    
                    <button 
                      onClick={() => {
                        if (window.confirm('Are you sure you want to clear your cart?')) {
                          clearCart();
                          setCartItems([]);
                        }
                      }}
                      className="text-gray-600 hover:text-accent-color transition-colors"
                    >
                      {t('cart.clear_cart')}
                    </button>
                  </div>
                </div>

                {/* Order Summary */}
                <div className="lg:w-1/3">
                  <div className="bg-white rounded-lg shadow-sm p-6 border border-gray-200">
                    <h2 className="text-xl font-bold mb-6">{t('cart.order_summary')}</h2>
                    
                    <div className="space-y-4 mb-6">
                      <div className="flex justify-between">
                        <span className="text-gray-600">{t('cart.subtotal')}</span>
                        <span className="font-semibold">{formatPrice(calculateTotal())} €</span>
                      </div>
                      
                      <div className="border-t border-gray-200 pt-4 mt-4">
                        <div className="flex justify-between">
                          <span className="font-bold">{t('cart.total')}</span>
                          <span className="font-bold text-xl">{formatPrice(calculateTotal())} €</span>
                        </div>
                        <p className="text-xs text-gray-500 mt-2 italic flex items-start">
                          <Info size={12} className="mr-1 mt-0.5 flex-shrink-0 text-accent-color" />
                          {t('cart.listed_prices_notice')}
                        </p>
                      </div>
                    </div>

                    {/* Request Quote Button */}
                    <Link 
                      href="/request-quote"
                      className="block w-full py-3 px-4 bg-accent-color text-white text-center font-bold rounded-md hover:bg-orange-600 transition-colors duration-300"
                    >
                      {t('cart.request_quote')}
                    </Link>
                    
                    {/* Info Notice */}
                    <div className="mt-6 bg-gray-50 p-4 rounded-md text-sm">
                      <div className="flex">
                        <Info size={16} className="text-accent-color mr-2 flex-shrink-0 mt-0.5" />
                        <p>
                          {t('cart.request_quote_notice')}
                        </p>
                      </div>
                    </div>
                    
                    {/* Shipping Notice */}
                    <div className="mt-4 text-sm text-gray-600">
                      <p>
                        {t('cart.shipping_information_notice')}
                        <Link href="/delivery" className="text-accent-color hover:underline">
                          {' '}{t('cart.delivery_page')}
                        </Link>.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </>
        ) : (
          <div className="text-center py-16">
            <div className="animate-pulse">
              <div className="h-8 bg-gray-200 rounded w-1/3 mx-auto mb-4"></div>
              <div className="h-4 bg-gray-200 rounded w-1/4 mx-auto"></div>
            </div>
          </div>
        )}
      </div>
    </>
  );
}

// Use Layout component as the page layout
Cart.getLayout = function getLayout(page) {
  return <Layout>{page}</Layout>;
}; 